import React from "react";
import PropTypes from 'prop-types';

//date and date picker imports
import { DateTime } from "luxon";
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker as MUIDatePicker} from '@mui/x-date-pickers/DatePicker';

export default function DatePicker(props){
    let { selected, minDate, maxDate, onAccept } = props;
    const usesJSDate = (selected instanceof Date || minDate instanceof Date || maxDate instanceof Date) ? true : false;
    
    //convert JS Dates to Luxon DateTime
    selected = selected instanceof Date ? DateTime.fromJSDate(selected) : selected;
    minDate = minDate instanceof Date ? DateTime.fromJSDate(minDate) : minDate;
    maxDate = maxDate instanceof Date ? DateTime.fromJSDate(maxDate) : maxDate;

    //set selected undefined so placeholder text is displayed on disabled
    if(props.inputDisabled && props.placeholder !== "")selected = undefined;

    const handleChange = (ts) => {
        if(usesJSDate)onAccept(ts.toJSDate());
        else onAccept(ts);
    }
    
    return(
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <MUIDatePicker
                showDaysOutsideCurrentMonth={true}
                value={selected}
                disabled={props.inputDisabled}
                label={props.label}
                minDate={minDate}
                maxDate={maxDate}
                onAccept={handleChange} // Process the new date only when the user selects a specific day or closes the date picker - prevents sending data when the user selects a different month or year
                views={["year", "month", "day"]}
                format={props.dateFormat}
                reduceAnimations={true}
                slotProps={{
                    openPickerButton: {
                        size: "small",
                    },
                    inputAdornment: {
                        position: "start",
                        sx: {
                            margin: "0px",
                            width: "1rem",
                            paddingLeft: "0.5rem",
                            paddingRight:"0.5rem",
                            "& .MuiTouchRipple-root": { // hide the touch ripple effect
                                color:"#00000000"
                            },
                            "& .MuiButtonBase-root": {
                                padding: "0px"
                            },
                            "& .MuiButtonBase-root:hover": { // style the hover
                                backgroundColor:"#00000000",
                                color:"#4a4a4a"
                            }
                        }
                    },
                    textField: {
                        readOnly: true,
                        placeholder:props.placeholder, 
                        sx: {
                            marginRight: props.textFieldMarginRight,
                            marginLeft: props.textFieldMarginLeft,
                            width: props.textFieldWidth,
                            '& .MuiOutlinedInput-root': {
                                height: props.textFieldHeight,
                                '& fieldset': {
                                    borderWidth: props.borderWidth,
                                    borderColor: props.borderColor,
                                },
                                '&:hover fieldset': {
                                    borderColor: props.inputDisabled ? "none" : props.highlightColor,
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: props.highlightHoverColor,
                                }
                            },
                            "& .MuiInputBase-input": { // style the input
                                paddingBlock:"0.45rem",
                                paddingRight:"0.5rem",
                                paddingLeft:"0px",
                                lineHeight:"1rem",
                                textAlign: "right"
                            },
                            "& .MuiInputBase-root": { // more input styling
                                backgroundColor: props.textFieldBackgroundColor, 
                                color: props.textColor,
                                padding:"0px",
                                width:"auto"
                            }
                        }                            
                    },
                    popper: {
                        placement: props.popperPlacement,
                        sx: {
                            "& .MuiTouchRipple-root": { // hide the touch ripple effect
                                color:"#00000000"
                            },
                            "& .MuiPaper-root": { //turn off the shadow effect
                                boxShadow:"none"
                            },
                            "& .MuiDateCalendar-root": { // allow the calendar to auto-adjust sizing
                                width:"15rem",
                                height:"auto",
                                margin:"0px",
                                maxHeight:"none" // accommodate browser-level font size adjustments
                            },
                            "& .MuiDayCalendar-slideTransition": {  // remove unnecessary space at the bottom of the popper
                                height:"auto",
                                minHeight:"0px"
                            },
                            "& .MuiPickersPopper-paper": { // fix the width of the popper, set background to white, and style the border
                                backgroundColor: "#ffffff",
                                border: "0.1rem solid #aeaeae",
                                borderRadius: "0.5rem",
                                marginBlock: props.popperMarginBlock, // set how far above/below the input element the popper will appear
                            },
                            "& .MuiPickersCalendarHeader-switchViewButton": { // reduce view arrow padding, and style the hover
                                padding:"0px",
                                "&:hover": {
                                    backgroundColor:"#00000000",
                                    color:"#4a4a4a"
                                },
                            },
                            "& .MuiPickersArrowSwitcher-button": { // reduce month switcher arrows padding, and style the hover
                                padding:"0px",
                                "&:hover": {
                                    backgroundColor:"#00000000",
                                    color:"#4a4a4a"
                                },
                            },                        
                            "& .MuiPickersCalendarHeader-label": { // remove excess space between the label and the view arrow
                                marginRight:"0px"
                            },
                            "& .MuiPickersCalendarHeader-root": { // style the header
                                backgroundColor: "#f0f0f0",
                                borderRadius: "0.5rem 0.5rem 0rem 0rem",
                                color: "#000000de",
                                borderBottom: "0.1rem solid #aeaeae",
                                padding: "0.2rem 0.1rem 0.2rem 0.5rem",
                                margin: "0px",
                                minHeight:"none" // accommodate browser-level font size adjustments
                            },
                            "& .MuiYearCalendar-root": { //allow the year section to auto-adjust sizing
                                width:"auto",
                                height:"auto",
                                maxHeight:"15rem",
                                padding: "0.3rem"
                            },
                            "& .MuiMonthCalendar-root": { //allow the month section to auto-adjust sizing
                                width:"auto",
                                height:"auto",
                                padding: "0.3rem"
                            },
                            "& .MuiDayCalendar-root": { // allow the day section to auto-adjust sizing
                                width:"auto",
                                height:"auto",
                                padding: "0.3rem"
                            },
                            "& .MuiPickersYear-yearButton": { // style the year pickers
                                padding:"0px",
                                margin:"0px",
                                borderRadius:"0.5rem",
                                width:"auto",
                                height:"auto",
                                paddingInline:"0.4rem",
                                marginBlock:"0.3rem",
                                "&:hover": {
                                    backgroundColor: "#f0f0f0",
                                },
                                "&.Mui-selected": {
                                    backgroundColor: props.highlightColor
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: props.highlightHoverColor
                                },
                                
                            },
                            "& .MuiPickersMonth-root": { // style the month pickers
                                flexBasis:"25%",
                                "& .MuiPickersMonth-monthButton": {
                                    padding:"0px",
                                    margin:"0px",
                                    borderRadius:"0.4rem",
                                    width:"auto",
                                    height:"auto",
                                    paddingInline:"0.5rem",
                                    marginBlock:"0.5rem",
                                    "&:hover": {
                                        backgroundColor: "#f0f0f0",
                                    },
                                    "&.Mui-selected": {
                                        backgroundColor: props.highlightColor
                                    },
                                    "&.Mui-selected:hover": {
                                        backgroundColor: props.highlightHoverColor
                                    },
                                },
                            },
                            "& .MuiPickersDay-root": { // style the day pickers 
                                margin:"0.125rem",
                                width: "1.75rem",
                                height: "1.75rem",
                                lineHeight: "1rem",
                                borderRadius: "0.4rem",
                                "&.MuiPickersDay-today": {
                                    border: "none",
                                    fontWeight:"bold"
                                },
                                "&:hover": {
                                    backgroundColor: "#f0f0f0",
                                },
                                "&.Mui-selected": {
                                    backgroundColor: props.highlightColor
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: props.highlightHoverColor
                                }
                            },
                            "& .MuiDayCalendar-weekDayLabel": { // style the day of week letters to match the day pickers
                                margin:"0.125rem",
                                width: "1.75rem",
                                height: "1.75rem"
                            },
                        }
                    }
                }}
                />
        </LocalizationProvider>
    )
}

DatePicker.propTypes = {
    selected: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(DateTime)]),
    minDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(DateTime)]),
    maxDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.instanceOf(DateTime)]),
    dateFormat:PropTypes.string,
    onAccept: PropTypes.func,
    textColor: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.string,
    textFieldBackgroundColor: PropTypes.string,
    textFieldWidth: PropTypes.string,
    textFieldHeight: PropTypes.string,
    popperPlacement: PropTypes.oneOf(['auto-end','auto-start','auto','bottom-end','bottom-start','bottom','left-end','left-start','left','right-end','right-start','right','top-end','top-start','top']),
    inputDisabled: PropTypes.bool,
    highlightColor: PropTypes.string,
    highlightHoverColor: PropTypes.string,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    popperMarginBlock: PropTypes.string,
    textFieldMarginLeft: PropTypes.string,
    textFieldMarginRight: PropTypes.string
}

DatePicker.defaultProps = {
    selected: new Date(),
    minDate: new Date('1970-01-01'),
    maxDate: new Date('2050-12-31'),
    dateFormat: "MM/dd/yyyy",
    onAccept: () => {},
    textColor: "rgba(0,0,0,1)",
    textFieldBackgroundColor: "transparent",
    textFieldWidth: "8rem",
    textFieldHeight: "2.5rem",
    popperPlacement: 'auto',
    inputDisabled: false,
    highlightColor: "blue",
    highlightHoverColor: "darkblue",
    label: "",
    placeholder: "",
    popperMarginBlock: "0.4rem"
}